import { getFirebaseAuthToken } from '../../utils/firebaseAuthToken';

const STORY_GUIDE_URL = process.env.REACT_APP_STORY_GUIDE_STREAM_URL
  || 'https://us-central1-ai-tourguide-1dc92.cloudfunctions.net/storyGuideStream';

const cleanUrl = (value) => {
  try {
    const url = new URL(String(value || ''));
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return '';
  }
};

export const previewLanguageCode = (value) => {
  const normalized = String(value || 'en').trim().toLowerCase().replace('_', '-');
  if (normalized.startsWith('pt-br') || normalized === 'br') return 'br';
  if (normalized.startsWith('pt')) return 'pt';
  if (normalized.startsWith('fr')) return 'fr';
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('bg')) return 'bg';
  if (normalized.startsWith('de')) return 'de';
  if (normalized.startsWith('it')) return 'it';
  if (normalized.startsWith('ar')) return 'ar';
  return 'en';
};

export const parsePreviewSseBlock = (block) => {
  let event = 'message';
  const data = [];
  String(block || '').split(/\r?\n/).forEach((line) => {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
  });
  if (!data.length || data.join('\n') === '[DONE]') return null;
  return { event, data: JSON.parse(data.join('\n')) };
};

export const consumePreviewSse = async (response, onEvent = () => {}) => {
  if (!response.body?.getReader) {
    throw new Error('This browser cannot read streamed AI previews.');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let doneEvent = null;
  while (true) {
    const result = await reader.read();
    buffer += decoder.decode(result.value || new Uint8Array(), { stream: !result.done })
      .replace(/\r\n/g, '\n');
    let separator = buffer.indexOf('\n\n');
    while (separator >= 0) {
      const block = buffer.slice(0, separator).trim();
      buffer = buffer.slice(separator + 2);
      if (block) {
        const parsed = parsePreviewSseBlock(block);
        if (parsed) {
          onEvent(parsed);
          if (parsed.event === 'error') {
            throw new Error(parsed.data?.message || 'AI preview generation failed.');
          }
          if (parsed.event === 'done') doneEvent = parsed.data;
        }
      }
      separator = buffer.indexOf('\n\n');
    }
    if (result.done) break;
  }
  if (!doneEvent) throw new Error('AI preview ended before completion.');
  return doneEvent;
};

export const buildPreviewRequest = ({
  packageData,
  runtimePackageUrl,
  opportunity,
  selectedTopicIds = [],
  depth = 'standard',
  runId,
  namespace,
}) => {
  const manifest = packageData?.manifest || {};
  const opportunityId = String(opportunity?.opportunity_id || '').trim();
  if (!opportunityId) throw new Error('Choose a story opportunity to preview.');
  if (!String(runtimePackageUrl || '').trim()) {
    throw new Error('Publish or load a runtime package before running a real preview.');
  }
  if (!String(namespace || '').trim()) {
    throw new Error('REACT_APP_AI_GUIDE_PREVIEW_NAMESPACE is not configured.');
  }
  const version = Number.parseInt(manifest.content_version, 10);
  const topics = [...new Set(selectedTopicIds.map(String).filter(Boolean))].slice(0, 12);
  const experienceRequest = {
    version: 1,
    intent: 'initial_story',
    selected_topic_ids: topics,
    topic_affinities: Object.fromEntries(topics.map((topicId) => [topicId, 1])),
    selected_lens_id: null,
    depth: ['brief', 'standard', 'deep'].includes(depth) ? depth : 'standard',
    recordings_heard: [],
    explanations_presented: [],
  };
  const storyPlan = {
    opportunityId,
    title: opportunity.discovery_title || opportunityId,
    centralQuestion: opportunity.central_question || '',
    whyThisFitsVisitor: topics.length
      ? `Preview profile topics: ${topics.join(', ')}`
      : 'Neutral Builder preview profile.',
    openingHook: opportunity.teaser || '',
    sourceStopIds: opportunity.anchor_stop_ids || [],
    claimIds: opportunity.claim_refs || [],
    estimatedDurationSeconds: 120,
    knowledgePolicy: 'official',
    treasureVersion: `${packageData.schema_version}:${version}`,
    acts: ['hook', 'explanation', 'payoff'],
    generatedAt: new Date().toISOString(),
    promptVersion: 16,
  };
  return {
    promptType: 'ai4PersonalStory',
    language: previewLanguageCode(manifest.language),
    variables: {
      guide_name: manifest.guide_name || manifest.guide_id,
      story_plan: JSON.stringify(storyPlan),
      visitor_mind: JSON.stringify({ selected_topic_ids: topics }),
      word_count: '180 to 240',
      chip_count: '4',
      user_question: `Tell the approved story and resolve this question: ${storyPlan.centralQuestion}`,
      poi_name: storyPlan.title,
      selected_story_title: storyPlan.title,
      selected_story_promise: storyPlan.centralQuestion,
      audience_context: 'Neutral editorial preview.',
      visit_context: 'No prior listening in this preview run.',
      grounding_mode: 'strict',
      interests: topics.join(', ') || 'neutral',
    },
    conversationHistory: [],
    requestMetadata: {
      guide_id: manifest.guide_id,
      guide_name: manifest.guide_name || manifest.guide_id,
      experience: 'builder_ai5_real_preview',
      usage_stage: 'builder_preview_story_text',
      generation_id: runId,
      preview_namespace: namespace,
      story_title: storyPlan.title,
      knowledge_policy: 'official',
      content_version: version,
    },
    contextRequest: {
      runtime_package_url: runtimePackageUrl,
      package_id: manifest.treasure_id,
      guide_id: manifest.guide_id,
      content_version: version,
      knowledge_policy: 'official',
      story_plan: storyPlan,
      visitor_context: JSON.stringify({ selected_topic_ids: topics }),
      previously_used_claim_ids: [],
      max_claims: 7,
      experience_request: experienceRequest,
    },
  };
};

const run = async ({ packageData, runtimePackageUrl, opportunity, selectedTopicIds, depth }) => {
  const namespace = process.env.REACT_APP_AI_GUIDE_PREVIEW_NAMESPACE;
  const startedAt = new Date();
  const runId = `builder_preview_${startedAt.getTime()}_${Math.random().toString(36).slice(2, 9)}`;
  if (!String(namespace || '').trim()) {
    throw new Error('REACT_APP_AI_GUIDE_PREVIEW_NAMESPACE is not configured.');
  }
  const request = buildPreviewRequest({
    packageData,
    runtimePackageUrl,
    opportunity,
    selectedTopicIds,
    depth,
    runId,
    namespace,
  });
  const token = await getFirebaseAuthToken();
  const artifactBase = {
    artifact_version: 'sofia_ai_preview_run.v1',
    run_id: runId,
    started_at: startedAt.toISOString(),
    preview_namespace: namespace,
    provider_output: false,
    package: {
      id: packageData.manifest?.treasure_id,
      guide_id: packageData.manifest?.guide_id,
      content_version: packageData.manifest?.content_version,
      sha256: null,
      runtime_url: cleanUrl(runtimePackageUrl),
    },
    profile: {
      selected_topic_ids: selectedTopicIds || [],
      depth,
    },
    opportunity_id: opportunity.opportunity_id,
    connected_recordings: (packageData.guide_shadow || [])
      .filter((shadow) => (opportunity.anchor_stop_ids || []).includes(shadow.stop_id))
      .map((shadow) => ({
        stop_id: shadow.stop_id,
        recorded_text: shadow.recorded_text || '',
        semantic_summary: shadow.semantic_summary || '',
        main_reveal: shadow.main_reveal || '',
        avoid_repeating: shadow.avoid_repeating || [],
      })),
    request: {
      story_plan: request.contextRequest.story_plan,
      experience_request: request.contextRequest.experience_request,
      prompt_type: request.promptType,
    },
  };
  try {
    const response = await fetch(STORY_GUIDE_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-Idempotency-Key': runId,
      },
      body: JSON.stringify({ ...request, authToken: token }),
    });
    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Story pipeline returned ${response.status}: ${body.slice(0, 500)}`);
    }
    const completed = await consumePreviewSse(response);
    return {
      ...artifactBase,
      package: {
        ...artifactBase.package,
        sha256: completed.contextPack?.manifest?.content_sha256 || null,
      },
      completed_at: new Date().toISOString(),
      status: 'completed',
      provider_output: true,
      output: {
        text: completed.text || '',
        suggestions: completed.chips || [],
        generation_id: completed.generationId || runId,
        effective_story_plan: completed.effectiveStoryPlan || null,
        context_pack: completed.contextPack || null,
        provenance: completed.provenance || completed.contextPack?.provenance || null,
        usage: completed.tokenInfo || null,
        cache: completed.contextPack?.retrieval?.package_cache_hit ?? null,
        cost: completed.cost || null,
      },
    };
  } catch (error) {
    error.previewArtifact = {
      ...artifactBase,
      completed_at: new Date().toISOString(),
      status: 'failed',
      failure: { message: error.message, code: error.code || null },
    };
    throw error;
  }
};

export const aiGuidePreviewService = { run };

export default aiGuidePreviewService;
